import React, { useState } from "react";
import { Box, Grid, TextField, Button, CircularProgress, Tabs, Tab, Stack, Typography } from "@mui/material";
import { Save, Cancel, CloudUpload, Code, Edit } from "@mui/icons-material";
import RichTextEditorCard from "./RichTextEditorCard";

function ContentForm({
  onSubmit,
  onCancel,
  editing,
  loading,
  register,
  handleSubmit,
  contentValue,
  setContentValue,
  setOpenRichEditor,
}) {
  const [editorMode, setEditorMode] = useState(0); // 0 = Rich Text, 1 = HTML Source / File Upload

  const handleHtmlFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result;
        if (typeof text === "string") {
          setContentValue(text);
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth label="Title" {...register("title", { required: true })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth label="Video Link" {...register("video_link")} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth label="Content ID" {...register("content_id", { required: true })} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth label="Subject" {...register("subject", { required: true })} />
        </Grid>
      </Grid>

      {/* Editor Selection Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs
          value={editorMode}
          onChange={(e, newMode) => setEditorMode(newMode)}
          sx={{
            "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
          }}
        >
          <Tab icon={<Edit fontSize="small" />} iconPosition="start" label="Rich Text Editor" />
          <Tab icon={<Code fontSize="small" />} iconPosition="start" label="HTML Code & File Upload" />
        </Tabs>
      </Box>

      {editorMode === 0 ? (
        /* Rich Text Editor Card */
        <RichTextEditorCard
          value={contentValue}
          onChange={setContentValue}
          onExpand={() => setOpenRichEditor(true)}
        />
      ) : (
        /* HTML File Upload & Source Editor */
        <Box sx={{ mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              sx={{ textTransform: "none", borderRadius: "8px" }}
            >
              Upload HTML File
              <input
                type="file"
                accept=".html,.htm"
                hidden
                onChange={handleHtmlFileUpload}
              />
            </Button>
            <Typography variant="caption" color="text.secondary">
              Select an .html file to import its content directly as raw HTML.
            </Typography>
          </Stack>

          <TextField
            fullWidth
            multiline
            rows={12}
            value={contentValue}
            onChange={(e) => setContentValue(e.target.value)}
            placeholder="Paste or write your raw HTML code here..."
            label="Raw HTML Content"
            InputProps={{
              sx: {
                fontFamily: "Courier New, monospace",
                fontSize: "0.9rem",
                borderRadius: "12px",
              }
            }}
          />
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 2 }}>
        <Button type="submit" variant="contained" color="primary" startIcon={<Save />} disabled={loading}>
          {editing ? "Update" : "Add"}
        </Button>
        {editing && (
          <Button type="button" variant="outlined" color="secondary" startIcon={<Cancel />} onClick={onCancel}>
            Cancel
          </Button>
        )}
        {loading && <CircularProgress size={24} />}
      </Box>
    </form>
  );
}

export default ContentForm;
